/**
 * Fallback data loader for client data
 */

import { formatCurrency } from '../../utils/formatUtils';
import { YEAR_CONFIG } from '../../config/yearConfig';

/**
 * Fallback method to load client data directly from Excel file
 * This is used if the API is unavailable
 * @returns {Promise<Array>} The loaded client data
 */
import { loadSponsorData } from './sponsorDataLoader';
import { mergeSponsorsIntoClients } from '../data-processing/sponsorMappingService';
import { loadConferenceSalesData } from './conferenceSalesDataLoader';

const generateConfiguredYearSequence = (descending = true) => {
  const start = Number(YEAR_CONFIG.baseYear);
  const end = Number(YEAR_CONFIG.lastDataYear);

  if (!Number.isInteger(start) || !Number.isInteger(end) || start > end) {
    return [];
  }

  const years = [];
  for (let year = start; year <= end; year++) {
    years.push(String(year));
  }

  return descending ? years.reverse() : years;
};

export const loadClientDataFallback = async () => {
  try {
    // Import ExcelJS for secure Excel parsing
    const ExcelJS = await import('exceljs');

    const res = await fetch('/protfolio.xlsx');
    if (!res.ok) throw new Error(res.statusText);

    const arrayBuffer = await res.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(arrayBuffer);

    // Get the first sheet
    const worksheet = workbook.worksheets[0];
    if (!worksheet) throw new Error('No worksheets found in Excel file');

    // Convert to JSON format similar to xlsx library
    const rawData = [];
    const headers = [];
    
    // Get headers from first row
    const headerRow = worksheet.getRow(1);
    headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      headers[colNumber - 1] = cell.value ? String(cell.value) : '';
    });
    
    // Process data rows
    worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
      if (rowNumber === 1) return; // Skip header row
      
      const rowData = {};
      row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        const header = headers[colNumber - 1];
        if (header) {
          // Convert cell value to string to preserve formatting
          rowData[header] = cell.value != null ? String(cell.value) : '';
        }
      });
      
      // Only add rows that have at least one non-empty value
      if (Object.values(rowData).some(val => val !== '')) {
        rawData.push(rowData);
      }
    });

    // Transform the data to match expected format
    const clients = transformExcelData(rawData);

    // Load sponsor data from the second sheet
    let sponsorError = null;
    let sponsorMap = {};
    try {
      const sponsorResult = await loadSponsorData(workbook);
      if (sponsorResult.error) {
        sponsorError = sponsorResult.error;
      } else {
        sponsorMap = sponsorResult.sponsorMap;
      }
    } catch (err) {
      sponsorError = 'Sponsor data loading error.';
    }

    // Load conference sales data from the third sheet
    let conferenceSalesData = {};
    try {
      conferenceSalesData = await loadConferenceSalesData(workbook);
    } catch (err) {
      console.error('Error loading conference sales data:', err);
      // Continue with empty conference sales data
    }

    // Merge sponsors into clients
    const clientsWithSponsors = mergeSponsorsIntoClients(clients, sponsorMap);

    // Attach sponsorError as a property for UI to consume
    clientsWithSponsors.sponsorError = sponsorError;
    
    // Attach conference sales data as a property for UI to consume
    clientsWithSponsors.conferenceSalesData = conferenceSalesData;

    return clientsWithSponsors;
  } catch (error) {
    console.error('Error loading client data from fallback method:', error);
    throw error;
  }
};

/**
 * Transform Excel data to match expected format
 * @param {Array} excelData - The raw Excel data
 * @returns {Array} The transformed client data
 */
const transformExcelData = (excelData) => {
  return excelData.map((row, index) => {
    // Format revenue as currency string to match existing UI expectations
    const formatRevenue = (value) => {
      if (!value || value === 0) return '$0.00';
      return formatCurrency(value);
    };

    // Find the last booking revenue from the most recent year with revenue
    const findLastBookingRevenue = (row) => {
      const years = generateConfiguredYearSequence(true); // Check from newest to oldest
      
      for (const year of years) {
        const revenueColumn = `${year} Revenue`;
        const revenue = row[revenueColumn];
        
        // Check if this year has revenue (non-zero and not empty)
        if (revenue && parseFloat(revenue) > 0) {
          return formatRevenue(revenue);
        }
      }
      
      // If no revenue found in any year, return $0.00
      return '$0.00';
    };

    // Find the last booked year
    const findLastBookedYear = (row) => {
      const years = generateConfiguredYearSequence(true); // Check from newest to oldest
      
      for (const year of years) {
        const revenueColumn = `${year} Revenue`;
        const revenue = row[revenueColumn];
        
        // Check if this year has revenue (non-zero and not empty)
        if (revenue && parseFloat(revenue) > 0) {
          return year;
        }
      }
      
      // If no revenue found in any year, return default
      return String(YEAR_CONFIG.defaultAllYearsSortYear);
    };

    // Create the base object with core mapping
    const transformedRow = {
      // Core mapping
      client_name: row['Customer'],
      conference_manager: row['Assigned to'],
      country: row['Country'],
      group_size: parseInt(row['Total Rooms']) || 0,
      latitude: row['Latitude found'],
      longitude: row['Longitude found'],
      
      // Financial data
      'Total Revenue': formatRevenue(row['Total Revenue']),
      
      // Calculate actual last booking revenue from most recent year with revenue
      'Last Booking Revenue': findLastBookingRevenue(row),
      'Last Booked': findLastBookedYear(row),
      'Key Contact Name': 'Contact Representative',
      'Key Contact Email': `contact@${(row['Customer'] || '').toLowerCase().replace(/[^a-z0-9]/g, '')}.com`,
      'Previously Assigned Manager': 'None',
      
      // New fields available for future use
      'Time zone': row['Time zone'],
      'Tier': row['Tier'],
      'Segment': row['Last booked conference'],
      'RN': row['Total RN'] || 'N/A',
      'Country matched': row['Country matched']
    };

    // Preserve all configured year-specific columns (e.g. Rooms, RN, Revenue)
    const years = generateConfiguredYearSequence(false);
    const metrics = YEAR_CONFIG.yearlyMetrics;
    
    years.forEach(year => {
      // Map the 1/0 columns for "booked only one conference" (these are just the year numbers as columns)
      if (row[year] !== undefined) {
        transformedRow[year] = row[year];
      }
      metrics.forEach(metric => {
        const columnName = `${year} ${metric}`;
        if (row[columnName] !== undefined) {
          if (metric === 'Revenue') {
            // Format revenue columns as currency
            transformedRow[columnName] = formatRevenue(row[columnName]);
          } else {
            // Keep rooms and RN as numbers
            transformedRow[columnName] = row[columnName] || 0;
          }
        }
      });
    });

    // Map "Attend to only one conference" column
    if (row['Attend to only one conference'] !== undefined) {
      transformedRow['Attend to only one conference'] = row['Attend to only one conference'];
    }

    // Dynamically preserve all % change columns for rooms, RN, and revenue
    Object.keys(row).forEach((col) => {
      if (
        /^% change \d{4}\/\d{4} rooms$/i.test(col) ||
        /^% change \d{4}\/\d{4} RN$/i.test(col) ||
        /^% change \d{4}\/\d{4} revenue$/i.test(col)
      ) {
        transformedRow[col] = row[col];
      }
    });

    // Preserve Sales Goal column (no year prefix)
    if (row['Sales Goal'] !== undefined) {
      transformedRow['Sales Goal'] = formatRevenue(row['Sales Goal']);
    }

    return transformedRow;
  });
};
